import {createContext, ReactNode, useCallback, useContext, useEffect, useState} from "react";
import {isSupported ,getMessaging, getToken, MessagePayload, onMessage} from "firebase/messaging";
import app from "../config/firebase";
import {api} from "../services/Api";

/**
 * Inscrição/remoção nos tópicos de push passa pela API do Superger com o JWT do cliente
 * (v1/pessoas/clientes/notificacoes/*). A API valida que os tópicos são do próprio cliente e fala com o
 * servidor de notificações usando um segredo que fica só no servidor. Antes o portal chamava o io.superger
 * direto, com o segredo embutido no bundle.
 */
export function alterarTopicos(jwt: string, inscrever: boolean, tokenFirebase: string, topicos: string[]) {
	return api.post(
		'v1/pessoas/clientes/notificacoes/' + (inscrever ? 'inscrever' : 'desinscrever'),
		{token_firebase: tokenFirebase, topicos: topicos},
		{headers: {'Authorization': 'Bearer ' + jwt, 'Content-Type': 'application/json'}}
	);
}

interface NotificationsProps {
	collapseKey: string;
	data: {};
	from: string;
	messageId: string;
	notification: { title: string, body: string };
	read: boolean;
}

type NotificationsContextData = {
	notificationsUnread: number;
	handleSetNotificationsUnread: (data: number) => void;
	notifications: NotificationsProps[] | null;
	handleSetNotifications: (data: NotificationsProps[] | null) => void;
	subscribe: (topic: any, jwt: string) => void;
	unsubscribe: (jwt?: string | null) => void;
}

type NotificationsContextProviderProps = {
	children: ReactNode;
}

const NotificationsContext = createContext<NotificationsContextData>({} as NotificationsContextData);

/**
 * O Firebase Messaging exige service worker, que só existe em contexto seguro (https ou localhost).
 * Acessando o portal por http num IP (ex.: totem apontando para http://<ip-do-pc>:3000/totem em
 * desenvolvimento) o getMessaging lança erro e derrubava a página inteira; sem ele o resto segue normal.
 */
function obterMessaging() {
	if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
		return null;
	}
	try {
		return getMessaging(app);
	} catch (e) {
		return null;
	}
}

export function NotificationsContextProvider(props: NotificationsContextProviderProps) {

	const messaging = obterMessaging();

	const [notifications, setNotifications] = useState<NotificationsProps[] | null>(null);
	const [notificationsUnread, setNotificationsUnread] = useState<number>(0);

	const handleSetNotifications = useCallback((data: NotificationsProps[] | null) => {
		setNotifications(data);
	}, []);

	const handleSetNotificationsUnread = useCallback((data: number) => {
		setNotificationsUnread(data);
	}, []);

	const handleLoad = useCallback(() => {
		let thisDB: any;
		let objStore: any;
		let request: any;
		let transaction;

		if ("indexedDB" in window) {
			let openRequest = indexedDB.open("notificationsNetcomet", 1);

			openRequest.onupgradeneeded = function (e: any) {
				thisDB = e?.target.result;
				if (!thisDB.objectStoreNames.contains("notificationsNetcomet")) {
					thisDB.createObjectStore("notificationsNetcomet", {autoIncrement: true});
					thisDB.onerror = function (error: any) {
						console.log(error)
					}
				}
			}
			openRequest.onsuccess = function (e: any) {
				thisDB = e.target.result;
				if (thisDB.objectStoreNames.contains("notificationsNetcomet")) {
					transaction = thisDB.transaction(["notificationsNetcomet"], "readwrite");
					objStore = transaction.objectStore("notificationsNetcomet");
					request = objStore.getAll();
					request.onsuccess = function (data: any) {
						if (data.target.result) {
							let unread = 0;
							data.target.result?.forEach((item: any) => {
								if (!item['read']) {
									unread++;
								}
							})
							handleSetNotifications(data.target.result);
							handleSetNotificationsUnread(unread);
						}
					}
					request.onerror = function (error: any) {
						console.log(error)
					}
				}
			}
			openRequest.onerror = function (error: any) {
				console.log(error)
			}
		}
	}, [handleSetNotificationsUnread, handleSetNotifications]);


	useEffect(() => {
		const handlePageLoad = () => {
			handleLoad();
		};

		window.addEventListener("load", handlePageLoad);

		window.addEventListener("focus", handlePageLoad);

		document.addEventListener("visibilitychange", () => {
			if (document.visibilityState === "visible") {
				handlePageLoad();
			}
		});

		return () => {
			window.removeEventListener("load", handlePageLoad);
			window.removeEventListener("focus", handlePageLoad);
			document.removeEventListener("visibilitychange", handlePageLoad);
		};
	}, [handleLoad]);

	if (messaging) onMessage(messaging, async function (payload: any) {
		let thisDB;
		let objStore: { getAll: () => any; add: (arg0: MessagePayload) => any; };
		let request;
		let transaction;
		let requestAux;

		if("indexedDB" in window) {
			let openRequest = indexedDB.open("notificationsNetcomet",1);

			openRequest.onupgradeneeded = function (e: any) {
				thisDB = e?.target.result;
				if (!thisDB.objectStoreNames.contains("notificationsNetcomet")) {
					thisDB.createObjectStore("notificationsNetcomet", {autoIncrement: true});
					thisDB.onerror = function (error: any) {
						console.log(error)
					}
				}
			}

			openRequest.onsuccess = function(e: any) {
				if (e) {
					thisDB = e.target.result;
					transaction = thisDB.transaction(["notificationsNetcomet"],"readwrite");
					objStore = transaction.objectStore("notificationsNetcomet");
					request = objStore.getAll();
					request.onsuccess = function () {
						payload['read'] = false;
						request = objStore.add(payload);
						request.onsuccess = function () {
							requestAux = objStore.getAll();
							requestAux.onsuccess = function (data: { target: { result: NotificationsProps[] | null; }; }) {
								handleSetNotifications(data.target.result);
								handleSetNotificationsUnread(notificationsUnread + 1);
							}
							requestAux.onerror = function (error: any) {
								console.log(error)
							}
						}
						request.onerror = function (error: any) {
							console.log(error)
						}
					}
					request.onerror = function (error: any) {
						console.log(error)
					}
				}

			}
			openRequest.onerror = function(error) {
				console.log(error);
			}
		}
	});

	async function subscribe(topics: any, jwt: string){
		const supported = await isSupported().catch(() => false)
		if (supported && messaging && jwt) {
			window.navigator.serviceWorker.register('./firebase-messaging-sw.js')
				.then(async () => {
					return window.navigator.serviceWorker.ready;
				})
				.then((registration) => {
					getToken(messaging, {
						serviceWorkerRegistration: registration
					}).then(async (currentToken)=>{
						if (currentToken) {
							localStorage.setItem('session_netcomet_token', currentToken);
							localStorage.setItem('session_netcomet_topics', JSON.stringify(topics));

							alterarTopicos(jwt, true, currentToken, topics).catch((erro) => {
								console.log(erro)
							});
						}
					}).catch((erro) => {
						console.log(erro)
					});
				})
				.catch((err) => {
					console.error('Erro ao registrar o Service Worker:', err);
				});

		}
	}

	async function unsubscribe(jwt?: string | null){
		const tokenFirebase = localStorage.getItem('session_netcomet_token');
		if (Notification.permission === 'granted' && jwt && tokenFirebase) {
			alterarTopicos(jwt, false, tokenFirebase, JSON.parse(localStorage.getItem('session_netcomet_topics') || '[]')).catch((erro) => {
				console.log(erro)
			});
		}
	}

	const value = {
		notifications,
		handleSetNotifications,
		notificationsUnread,
		handleSetNotificationsUnread,
		subscribe,
		unsubscribe
	}

	return (
		<NotificationsContext.Provider value={value}>
			{ props.children }
		</NotificationsContext.Provider>
	)
}

export function useNotifications(){
	return useContext(NotificationsContext);
}
